import { useEffect, useState } from "react";
import { Sparkles, Check, X, Loader2, Plus } from "lucide-react";
import api from "../lib/api";

const ADD_PRESETS = ["sunglasses", "party hat", "crown", "mustache", "bow tie", "cool headphones"];

// Step-2 feature chips: KEEP/DROP the traits we detected, and ADD fun extras the photo lacks.
// Lifts { dropped, added } up so the approve card turns them into a render correction note.
export const TraitToggles = ({ creationId, value, onChange, onRecommend, disabled }) => {
  const { dropped, added } = value;
  const [traits, setTraits] = useState(null); // null = loading, [] = none found
  const [draft, setDraft] = useState("");

  useEffect(() => {
    let alive = true;
    setTraits(null);
    onChange({ dropped: [], added: [] });
    api
      .get(`/creations/${creationId}/traits`)
      .then(({ data }) => {
        if (!alive) return;
        setTraits(data.traits || []);
        onRecommend?.(data.recommended_style || null);
      })
      .catch(() => alive && setTraits([]));
    return () => {
      alive = false;
    };
  }, [creationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleDrop = (t) =>
    onChange({ ...value, dropped: dropped.includes(t) ? dropped.filter((x) => x !== t) : [...dropped, t] });
  const toggleAdd = (t) =>
    onChange({ ...value, added: added.includes(t) ? added.filter((x) => x !== t) : [...added, t] });
  const addDraft = () => {
    const t = draft.trim().toLowerCase();
    if (t && !added.includes(t)) onChange({ ...value, added: [...added, t] });
    setDraft("");
  };

  return (
    <div data-testid="trait-toggles" className="space-y-5">
      <div>
        <p className="text-xs font-mono2 uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-accent" /> Detected features — tap to keep or drop
        </p>
        {traits === null ? (
          <div data-testid="trait-toggles-loading" className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Reading the distinctive features…
          </div>
        ) : traits.length === 0 ? (
          <p className="text-sm text-muted-foreground">No stand-out features detected — add a fun one below.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {traits.map((t) => {
              const off = dropped.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  data-testid={`trait-chip-${off ? "dropped" : "kept"}`}
                  aria-pressed={!off}
                  onClick={() => toggleDrop(t)}
                  disabled={disabled}
                  className={`rounded-full pl-3 pr-3.5 py-2 text-sm font-semibold border inline-flex items-center gap-1.5 transition-colors disabled:opacity-50 ${
                    off
                      ? "bg-muted text-muted-foreground border-border line-through decoration-2"
                      : "bg-primary/10 text-foreground border-primary/60 hover:border-primary"
                  }`}
                >
                  {off ? <X className="h-3.5 w-3.5 no-underline" /> : <Check className="h-3.5 w-3.5 text-accent" />}
                  {t}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <p className="text-xs font-mono2 uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
          <Plus className="h-3.5 w-3.5 text-accent" /> Add a fun feature
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          {ADD_PRESETS.map((t) => {
            const on = added.includes(t);
            return (
              <button
                key={t}
                type="button"
                data-testid={`trait-add-${on ? "on" : "off"}`}
                aria-pressed={on}
                onClick={() => toggleAdd(t)}
                disabled={disabled}
                className={`rounded-full pl-3 pr-3.5 py-2 text-sm font-semibold border inline-flex items-center gap-1.5 transition-colors disabled:opacity-50 ${
                  on ? "bg-accent text-accent-foreground border-accent" : "bg-background border-border hover:border-accent"
                }`}
              >
                {on ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                {t}
              </button>
            );
          })}
        </div>
        <div className="flex gap-2">
          <input
            data-testid="trait-add-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addDraft())}
            disabled={disabled}
            placeholder="e.g. eye patch, tiny scarf…"
            maxLength={40}
            className="flex-1 rounded-full border border-border bg-background px-4 py-2 text-sm outline-none focus:border-accent disabled:opacity-50"
          />
          <button
            type="button"
            data-testid="trait-add-submit"
            onClick={addDraft}
            disabled={disabled || !draft.trim()}
            className="rounded-full px-4 py-2 text-sm font-semibold border border-border hover:border-accent disabled:opacity-50"
          >
            Add
          </button>
        </div>
        {added.filter((t) => !ADD_PRESETS.includes(t)).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {added
              .filter((t) => !ADD_PRESETS.includes(t))
              .map((t) => (
                <span key={t} className="rounded-full bg-accent text-accent-foreground pl-3 pr-2 py-1.5 text-sm font-semibold inline-flex items-center gap-1.5">
                  {t}
                  <button type="button" onClick={() => toggleAdd(t)} disabled={disabled} className="hover:opacity-70" data-testid="trait-add-remove">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
          </div>
        )}
      </div>

      {(dropped.length > 0 || added.length > 0) && (
        <p data-testid="trait-change-hint" className="text-xs text-muted-foreground">
          {dropped.length > 0 && <>Dropping: {dropped.join(", ")}. </>}
          {added.length > 0 && <>Adding: {added.join(", ")}. </>}
          Hit <span className="font-semibold">Regenerate</span> or <span className="font-semibold">Compare Styles</span> to apply.
        </p>
      )}
    </div>
  );
};
